<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.title.managePermissions')"
    :header-shadow="true"
    @agree="savePermissions"
    agree-text="dialog.agreeSave"
    :loading="loading"
    dialog-width="80vw"
  >
    <template #body>
      <template v-if="isFetched">
        <v-col cols="12">
          <v-row no-gutters style="min-height: 36px;">
            <v-spacer> </v-spacer>
            <v-col cols="7" class="border--top" style="background-color: rgba(222, 222, 222, 0.17);">
              <v-row no-gutters>
                <v-col cols="6">
                  <v-row no-gutters class="justify-center align-center fill-height">
                    <v-col cols="auto" class="px-2 py-1">
                      <span class="label">
                        {{ $i18n.t('dialog.body.permissions') }}
                      </span>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <v-row no-gutters class="justify-center align-center fill-height">
                    <v-col cols="auto" class="px-3 py-1">
                      <span class="label">
                        {{ $i18n.t('dialog.body.commentsAndAttachments') }}
                      </span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <v-expansion-panels accordion multiple>
                <permissions-expansion-panel-layers
                  v-for="key in layersPermissionsKeys"
                  :level="0"
                  :key="key"
                  :header="$i18n.t(`dialog.body.permissionsGroups.${key}`)"
                  :items="getLayersItems(key)"
                ></permissions-expansion-panel-layers>
                <template v-for="key in otherPermissionsKeys">
                  <permissions-expansion-panel
                    :header="$i18n.t(`dialog.body.permissionsGroups.${key}`)"
                    :key="key"
                    :level="1"
                  >
                    <template #content>
                      <v-list dense class="pt-0">
                        <users-groups-permissions-dialog-tools-item
                          v-for="tool in permissionsSchema[key]"
                          :key="tool.label"
                          v-bind="tool"
                          group="modules"
                        >
                        </users-groups-permissions-dialog-tools-item>
                      </v-list>
                    </template>
                  </permissions-expansion-panel>
                </template>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-col>
      </template>
      <template v-else>
        <main-loader></main-loader>
      </template>
    </template>
  </main-large-dialog>
</template>

<script>
/* eslint-disable */
import { call, get, sync } from 'vuex-pathify';
import { cloneDeep } from 'lodash';

import MainLoader from '@/components/MainLoader';
import UsersGroupsPermissionsDialogToolsItem from '@/components/UsersGroupsPermissionsDialogToolsItem';
import PermissionsExpansionPanel from '@/components/PermissionsExpansionPanel';
import PermissionsExpansionPanelLayers from '@/components/PermissionsExpansionPanelLayers';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  components: {
    MainLoader,
    MainLargeDialog,
    PermissionsExpansionPanel,
    PermissionsExpansionPanelLayers,
    UsersGroupsPermissionsDialogToolsItem,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    groupId: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  data: () => ({
    isFetched: false,
    loading: false,
  }),
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    groupPermissions: get('users/groupsPermissions@:groupId'),
    toolsSchema: get('users/permissionsSchema@tools'),
    permissionsSchema: get('users/permissionsSchema'),
    layersPermissionsKeys() {
      return Object.keys(this.permissionsSchema).filter(key => key !== 'modules');
    },
    otherPermissionsKeys() {
      return Object.keys(this.permissionsSchema).filter(key => !key.includes('_layers'));
    },
    currentGroupPermissions: sync('users/currentGroupPermissions'),
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.init();
        } else {
          this.onDialogClosed();
        }
      },
    },
  },
  methods: {
    getPermissionsSchema: call('users/getPermissionsSchema'),
    getGroupPermissions: call('users/getGroupPermissions'),
    editGroupPermissions: call('users/editGroupPermissions'),
    getLayersItems(key) {
      return [
        ...this.permissionsSchema[key].groups,
        ...[
          {
            id: 0,
            name: this.$i18n.t('dialog.body.notAssignedLayers'),
            layers: this.permissionsSchema[key].layers,
            subgroups: [],
          },
        ],
      ];
    },
    async init() {
      try {
        await Promise.all([this.getPermissionsSchema(), this.getGroupPermissions(this.groupId)]);
        this.currentGroupPermissions = this.groupPermissions;
        setTimeout(() => {
          this.isFetched = true;
        }, 500);
      } catch (e) {
        console.log(e);
      }
    },
    async savePermissions() {
      try {
        this.loading = true;
        const currentGroupPermissionsCopy = cloneDeep(this.currentGroupPermissions);
        const body = Object.keys(currentGroupPermissionsCopy).reduce((total, current) => {
          const idKey = current === 'modules' ? 'module_name' : 'layer_id';
          const value = currentGroupPermissionsCopy[current].filter(tool => {
            const { main_value, attachments_and_comments_value } = this.groupPermissions[current].find(
              permission => permission[idKey] === tool[idKey]
            );
            return (
              tool.attachments_and_comments_value !== attachments_and_comments_value || tool.main_value !== main_value
            );
          });
          return { ...total, [current]: value };
        }, {});
        await this.editGroupPermissions({ id: this.groupId, body });
        this.loading = false;
        this.computedIsVisible = false;
      } catch (error) {
        console.log(error);
      }
    },
    onDialogClosed() {
      this.isFetched = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-expansion-panels {
  border-radius: 0;
}
.v-expansion-panel,
.border--top {
  @include border(right);
  @include border(left);
}
.border--top {
  border-radius: 6px 6px 0 0;
  @include border(top);
}
.label {
  font-size: 12px;
  line-height: 14px !important;
}
::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>
